"use client";

import { useEffect } from "react";
import type { AnalyticsEventType } from "@/firebase/analyticsEventTypes";
const EMBED_ANALYTICS_DEFER_MS = 4000;
export type LogAnalyticsEventClientOptions = {
  /**
   * When true, delay loading firebaseAnalytics until idle / timeout so Auth
   * does not compete with first media on Slow 3G embeds.
   */
  defer?: boolean;
};

/**
 * Log an analytics event only after mount, and only after dynamically loading
 * `firebaseAnalytics` so this module never pulls `firebaseConfig` (and `getAuth`)
 * during RSC / server evaluation — that was breaking `/embed/*` with a 500 when
 * the embed tree imported any client module that statically imported analytics.
 */
export function useLogAnalyticsEventClient(event: AnalyticsEventType, eventParams?: object, options?: LogAnalyticsEventClientOptions) {
  const defer = Boolean(options?.defer);
  useEffect(() => {
    let cancelled = false;
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    const fire = () => {
      if (cancelled) return;
      void import("@/firebase/firebaseAnalytics").then(({
        logAnalyticsEvent
      }) => {
        if (cancelled) return;
        void logAnalyticsEvent(event, eventParams);
      });
    };
    if (!defer) {
      fire();
      return () => {
        cancelled = true;
      };
    }
    if (typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(() => fire(), {
        timeout: EMBED_ANALYTICS_DEFER_MS
      });
    } else {
      timeoutId = window.setTimeout(fire, EMBED_ANALYTICS_DEFER_MS);
    }
    return () => {
      cancelled = true;
      if (idleId !== undefined && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mount (page view semantics)
  }, []);
  return null;
}